/**
 * Spinner Component - Premium Minimal
 *
 * Clean loading spinners. Subtle, not distracting.
 */

import { cn } from '../../utils/helpers'

export function Spinner({ size = 'md', className = '' }) {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8',
  }

  return (
    <svg
      className={cn('animate-spin text-[#b0b0b0]', sizes[size], className)}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-20"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-80"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

export function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fafafa]">
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          <div className="w-14 h-14 rounded-[6px] bg-[#0f0f0f] flex items-center justify-center">
            <span className="text-xl font-bold text-white">99</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          <span className="text-[#b0b0b0] text-sm">Loading...</span>
        </div>
      </div>
    </div>
  )
}

/**
 * ContentLoader - Minimal loader for page content area
 * Used with Suspense to show subtle loading while keeping sidebar visible
 */
export function ContentLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Spinner size="lg" className="text-[#d0d0d0]" />
    </div>
  )
}

/**
 * AdminLoadingScreen - Loading screen with sidebar placeholder
 * Used for admin page transitions to prevent jarring full-page flash
 */
export function AdminLoadingScreen() {
  return (
    <div className="min-h-screen bg-[#fafafa] flex">
      {/* Sidebar placeholder - matches actual sidebar width */}
      <div className="hidden md:block w-60 bg-white border-r border-[#ececec] flex-shrink-0" />
      {/* Content area with centered spinner */}
      <div className="flex-1 flex items-center justify-center">
        <Spinner size="lg" className="text-[#d0d0d0]" />
      </div>
    </div>
  )
}

/**
 * BuyerLoadingScreen - Loading screen with sidebar placeholder for buyer pages
 */
export function BuyerLoadingScreen() {
  return (
    <div className="min-h-screen bg-[#fafafa] flex">
      {/* Sidebar placeholder - matches actual sidebar width */}
      <div className="hidden md:block w-60 bg-white border-r border-[#ececec] flex-shrink-0" />
      {/* Content area with centered spinner */}
      <div className="flex-1 flex items-center justify-center">
        <Spinner size="lg" className="text-[#d0d0d0]" />
      </div>
    </div>
  )
}

/**
 * Skeleton loading components for premium minimal design
 */
export function Skeleton({ className = '' }) {
  return (
    <div className={cn('animate-pulse bg-[#f5f5f5] rounded', className)} />
  )
}

export function MetricCardSkeleton() {
  return (
    <div className="bg-white rounded-[6px] border border-[#ececec] p-5 shadow-sm">
      <Skeleton className="h-3 w-16 mb-3" />
      <Skeleton className="h-8 w-24 mb-2" />
      <Skeleton className="h-2.5 w-20" />
    </div>
  )
}

export function ChartSkeleton() {
  return (
    <div className="bg-white rounded-[6px] border border-[#ececec] p-5 shadow-sm">
      <Skeleton className="h-3 w-24 mb-4" />
      <div className="h-48 flex items-end gap-1">
        {Array.from({ length: 24 }).map((_, i) => (
          <Skeleton
            key={i}
            className="flex-1"
            style={{ height: `${Math.random() * 80 + 20}%` }}
          />
        ))}
      </div>
    </div>
  )
}

export function TableSkeleton({ rows = 5 }) {
  return (
    <div className="bg-white rounded-[6px] border border-[#ececec] p-5 shadow-sm">
      <Skeleton className="h-3 w-32 mb-4" />
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="w-8 h-8 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-2 w-20" />
            </div>
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  )
}
